import { useMemo, useState } from "react";
import { Link } from "@/lib/router-compat";
import { ArrowDown, ArrowUp, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { LiveToken } from "@/hooks/useLiveMarkets";
import { cdnImage } from "@/lib/images";
import { T } from "@/i18n/T";
import { useT } from "@/i18n/useLocale";

type SortKey =
  | "market_cap_rank"
  | "current_price"
  | "price_change_percentage_1h_in_currency"
  | "price_change_percentage_24h_in_currency"
  | "price_change_percentage_7d_in_currency"
  | "market_cap"
  | "total_volume"
  | "circulating_supply";

interface Props {
  tokens: LiveToken[];
  pricePulse: Map<string, "up" | "down">;
  watchlist: string[];
  onToggleWatch: (id: string) => void;
  loading?: boolean;
}

const fmtPrice = (p?: number | null) => {
  if (p == null) return "-";
  if (p >= 1) return `$${p.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  return `$${p.toLocaleString(undefined, { maximumFractionDigits: 6 })}`;
};
const fmtCompact = (n?: number | null) => {
  if (n == null) return "-";
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(2)}K`;
  return `$${n.toFixed(0)}`;
};
const fmtSupply = (n?: number | null, sym?: string) => {
  if (n == null) return "-";
  return `${n.toLocaleString(undefined, { maximumFractionDigits: 0 })} ${sym?.toUpperCase() ?? ""}`;
};

const PctCell = ({ value }: { value?: number | null }) => {
  if (value == null) return <span className="text-muted-foreground">, </span>;
  const positive = value >= 0;
  return (
    <span className={cn("inline-flex items-center gap-0.5 tabular-nums text-sm", positive ? "text-success" : "text-destructive")}>
      {positive ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
      {Math.abs(value).toFixed(2)}%
    </span>
  );
};

const Sparkline = ({ points, positive }: { points?: number[]; positive: boolean }) => {
  if (!points || points.length < 2) return null;
  const w = 120;
  const h = 36;
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const step = w / (points.length - 1);
  const d = points
    .map((p, i) => {
      const x = i * step;
      const y = h - ((p - min) / range) * h;
      return `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg width={w} height={h} className="block">
      <path d={d} fill="none" strokeWidth={1.5} className={positive ? "stroke-success" : "stroke-destructive"} />
    </svg>
  );
};

const Header = ({
  label,
  align = "right",
  sortKey,
  current,
  dir,
  onSort,
}: {
  label: string;
  align?: "left" | "right";
  sortKey?: SortKey;
  current: SortKey;
  dir: "asc" | "desc";
  onSort: (k: SortKey) => void;
}) => {
  const active = sortKey === current;
  return (
    <th
      className={cn(
        "px-3 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wide",
        align === "right" ? "text-right" : "text-left",
        sortKey && "cursor-pointer hover:text-foreground select-none",
      )}
      onClick={() => sortKey && onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && (dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}
      </span>
    </th>
  );
};

const PAGE = 50;

const MarketsTable = ({ tokens, pricePulse, watchlist, onToggleWatch, loading }: Props) => {
  const t = useT();
  const [sortKey, setSortKey] = useState<SortKey>("market_cap_rank");
  const [dir, setDir] = useState<"asc" | "desc">("asc");
  const [visible, setVisible] = useState(PAGE);

  const onSort = (k: SortKey) => {
    if (k === sortKey) {
      setDir(d => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(k);
      setDir(k === "market_cap_rank" ? "asc" : "desc");
    }
  };

  const sorted = useMemo(() => {
    const list = [...tokens];
    list.sort((a, b) => {
      const av = (a[sortKey] as number | null | undefined) ?? (dir === "asc" ? Infinity : -Infinity);
      const bv = (b[sortKey] as number | null | undefined) ?? (dir === "asc" ? Infinity : -Infinity);
      return dir === "asc" ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
    return list;
  }, [tokens, sortKey, dir]);

  const rows = sorted.slice(0, visible);

  return (
    <div className="rounded-xl border border-border/60 bg-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/30 border-b border-border/60">
            <tr>
              <th className="w-10 px-3 py-3" />
              <Header label="#" align="left" sortKey="market_cap_rank" current={sortKey} dir={dir} onSort={onSort} />
              <Header label={t("auto.name-49ee")} align="left" current={sortKey} dir={dir} onSort={onSort} />
              <Header label={t("auto.price-3601")} sortKey="current_price" current={sortKey} dir={dir} onSort={onSort} />
              <Header label="1h %" sortKey="price_change_percentage_1h_in_currency" current={sortKey} dir={dir} onSort={onSort} />
              <Header label="24h %" sortKey="price_change_percentage_24h_in_currency" current={sortKey} dir={dir} onSort={onSort} />
              <Header label="7d %" sortKey="price_change_percentage_7d_in_currency" current={sortKey} dir={dir} onSort={onSort} />
              <Header label={t("auto.market-cap-81c9")} sortKey="market_cap" current={sortKey} dir={dir} onSort={onSort} />
              <Header label={t("auto.volume-24h-828d")} sortKey="total_volume" current={sortKey} dir={dir} onSort={onSort} />
              <Header label={t("auto.circulating-supply-2a56")} sortKey="circulating_supply" current={sortKey} dir={dir} onSort={onSort} />
              <Header label={t("auto.last-7-days-4b29")} current={sortKey} dir={dir} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td colSpan={11} className="text-center py-16 text-muted-foreground"><T k="auto.loading-live-markets-f335" /></td></tr>
            )}
            {rows.map(t => {
              const pulse = pricePulse.get(t.id);
              const positive7d = (t.price_change_percentage_7d_in_currency ?? 0) >= 0;
              const watched = watchlist.includes(t.id);
              return (
                <tr key={t.id} className="border-b border-border/40 last:border-0 hover:bg-muted/30 transition-colors">
                  <td className="px-3 py-3">
                    <button
                      onClick={() => onToggleWatch(t.id)}
                      className="text-muted-foreground hover:text-warning transition-colors"
                      aria-label={watched ? "Remove from watchlist" : "Add to watchlist"}
                    >
                      <Star className={cn("h-4 w-4", watched && "fill-warning text-warning")} />
                    </button>
                  </td>
                  <td className="px-3 py-3 text-muted-foreground tabular-nums">{t.market_cap_rank}</td>
                  <td className="px-3 py-3">
                    <Link to={`/markets/${t.id}`} className="flex items-center gap-2 hover:text-primary">
                      <img src={cdnImage(t.image, { width: 24, height: 24, fit: "contain" })} alt="" className="h-6 w-6 rounded-full" loading="lazy" />
                      <span className="font-medium">{t.name}</span>
                      <span className="text-xs text-muted-foreground uppercase">{t.symbol}</span>
                    </Link>
                  </td>
                  <td className={cn(
                    "px-3 py-3 text-right font-medium tabular-nums transition-colors",
                    pulse === "up" && "text-success",
                    pulse === "down" && "text-destructive",
                  )}>
                    {fmtPrice(t.current_price)}
                  </td>
                  <td className="px-3 py-3 text-right"><PctCell value={t.price_change_percentage_1h_in_currency} /></td>
                  <td className="px-3 py-3 text-right"><PctCell value={t.price_change_percentage_24h_in_currency} /></td>
                  <td className="px-3 py-3 text-right"><PctCell value={t.price_change_percentage_7d_in_currency} /></td>
                  <td className="px-3 py-3 text-right tabular-nums">{fmtCompact(t.market_cap)}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{fmtCompact(t.total_volume)}</td>
                  <td className="px-3 py-3 text-right text-muted-foreground tabular-nums text-xs">{fmtSupply(t.circulating_supply, t.symbol)}</td>
                  <td className="px-3 py-3 text-right">
                    <div className="inline-flex justify-end">
                      <Sparkline points={t.sparkline_in_7d?.price} positive={positive7d} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {visible < sorted.length && (
        <div className="p-4 flex justify-center border-t border-border/60">
          <Button variant="outline" onClick={() => setVisible(v => v + PAGE)}>
            <T k="auto.show-more-146f" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default MarketsTable;
